/**
 * DB Step Executor — GraphQL query/mutation as a workflow step
 *
 * Config:
 * - operation: 'query' | 'mutation'
 * - query: GraphQL query/mutation string
 * - variables: Record<string, unknown> (supports {{variable}} interpolation)
 */

import type { EngineStep, EngineExecutionContext, StepExecutionResult, StepExecutor } from '../types';
import { graphqlQuery, graphqlMutation } from '../../../database/graphqlClient';

export const dbStepExecutors: StepExecutor[] = [
  {
    type: 'db_query',
    async execute(step: EngineStep, context: EngineExecutionContext): Promise<StepExecutionResult> {
      try {
        const query = step.config.query as string;
        const variables = (step.config.variables as Record<string, unknown>) || {};

        const response = await graphqlQuery<Record<string, unknown>>(query, variables);

        if (response.errors && response.errors.length > 0) {
          return {
            success: false,
            error: `GraphQL errors: ${response.errors.map((e: { message: string }) => e.message).join(', ')}`,
          };
        }

        return {
          success: true,
          output: { queryResult: response.data },
        };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'DB query failed',
        };
      }
    },
  },
  {
    type: 'db_mutation',
    async execute(step: EngineStep, context: EngineExecutionContext): Promise<StepExecutionResult> {
      try {
        const mutation = step.config.mutation as string || step.config.query as string;
        const variables = (step.config.variables as Record<string, unknown>) || {};

        const response = await graphqlMutation<Record<string, unknown>>(mutation, variables);

        if (response.errors && response.errors.length > 0) {
          return {
            success: false,
            error: `GraphQL errors: ${response.errors.map((e: { message: string }) => e.message).join(', ')}`,
          };
        }

        return {
          success: true,
          output: { mutationResult: response.data },
        };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'DB mutation failed',
        };
      }
    },
  },
];
